'use client';

import { ApplicationType, InterfaceType } from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import { useEffect, useMemo } from 'react';
import { useUserStore } from '../../../../auth/UserStoreProvider';
import useExecuteNode from '../../../../components/Copilot/hooks/useExecuteNode';
import useFetchPlatformAutomationConfig, {
  PlatformAutomationConfigType,
} from '../../../../platform-automation/hooks/useFetchPlatformAutomationConfig';
import { useInterfaceResourceStrategy } from '../../../context/InterfaceResourceStrategyProvider';
import { useGlobalAppStateStore } from '../../../stores/app/GlobalAppStateStore';
import { useGetDeviceDetailsInStateStore } from '../../../stores/GlobalStateStore';
import { InterfaceModes } from '../../../stores/InterfaceStore';
import InterfaceHelper from '../../../stores/InterfaceStore/helper/InterfaceHelper';
import { createWalkthroughInstanceEventBus } from '../../../types/walkthroughInstanceEventBus';
import useWalkthroughParameters from '../InterfacePageProviders/hooks/useWalkthroughParams';
import type { InterfacePageResourceLoaderProps } from './InterfacePageResourceLoader';

function WalkthroughClientLoader(
  props: Pick<InterfacePageResourceLoaderProps, 'response' | 'mode' | 'isPublic'>,
) {
  const { response, mode = InterfaceModes.RUNNER, isPublic } = props;
  const interfacePage = response.interfacePage;
  const interfacePageId = interfacePage?.id;
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const interfaceId = interfaceRecord?.id;
  const isLoggedIn = useUserStore().hooks.useIsLoggedIn();
  const componentType = interfacePage?.properties.componentType;
  const { strategy } = useInterfaceResourceStrategy();
  const isWebApplication =
    InterfaceHelper.getInterfacePlatform(interfaceRecord) === ApplicationType.WEB;
  const recordType = InterfaceHelper.getInterfaceType(interfaceRecord);
  const isWalkthroughSupported = isWebApplication && recordType === InterfaceType.Application;

  // only fetch in runner mode, when user is logged in or page is public, and when interface page type is PAGE
  const canFetch = (isPublic ?? false) || isLoggedIn;
  const shouldFetch =
    mode === InterfaceModes.RUNNER &&
    Boolean(interfacePageId) &&
    Boolean(interfaceId) &&
    canFetch &&
    componentType === InterfacePageEntityComponentType.PAGE &&
    isWalkthroughSupported;

  const {
    config,
    isLoading: isConfigLoading,
    error: configError,
  } = useFetchPlatformAutomationConfig(PlatformAutomationConfigType.InterfaceWalkthroughForPage, {
    enabled: shouldFetch,
  });

  const automationId = config?.automationId;
  const { mutate } = useExecuteNode();

  // instanceId === interfaceRecord?.id in no-code; generic hosts pass their own stable key
  const walkthroughInstanceEventBus = useMemo(() => {
    if (!interfaceId) return null;
    return createWalkthroughInstanceEventBus(interfaceId);
  }, [interfaceId]);

  const { device } = useGetDeviceDetailsInStateStore();

  const parameters = useWalkthroughParameters({
    interfacePageId,
    interfaceId,
    strategy,
    device,
  });

  useEffect(() => {
    if (!shouldFetch || !automationId || !interfacePageId || !walkthroughInstanceEventBus) {
      return;
    }

    mutate(
      {
        automationId,
        synchronous: true,
        parameters,
        meta: {
          isPublicPage: isPublic,
        },
      },
      {
        onSuccess: (data: {
          response?: { selectedWalkthrough: InterfacePageEntity | undefined };
        }) => {
          const response = data.response ?? { selectedWalkthrough: undefined };
          walkthroughInstanceEventBus.publish('onSetFetchedWalkthroughs', {
            pageId: interfacePageId,
            walkthrough: response.selectedWalkthrough,
          });
        },
        onError: () => {
          walkthroughInstanceEventBus.publish('onSetFetchedWalkthroughs', {
            pageId: interfacePageId,
            walkthrough: undefined,
          });
        },
      },
    );
  }, [
    shouldFetch,
    automationId,
    interfacePageId,
    walkthroughInstanceEventBus,
    mutate,
    parameters,
    isPublic,
  ]);

  // handle error when config is not found
  useEffect(() => {
    if (!isConfigLoading && !configError && !automationId && shouldFetch) {
      console.error(
        'Platform automation not found.',
        PlatformAutomationConfigType.InterfaceWalkthroughForPage,
      );
    }
  }, [isConfigLoading, configError, automationId, shouldFetch]);

  return null;
}

export default WalkthroughClientLoader;
